import React, { memo, useEffect, useState } from "react";
import classNames from "classnames";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import "./UserInfoCards.css";
import "./UserInfoCardsTheme2.css";
import { readEmploymentField } from "utils/customerEmployment";

const SURFACE_LIGHT = "#ffffff";
const SURFACE_DARK = "#272A3D";
const SURFACE_DARK_CUSTOMER = "#272A3D";
const NEUTRAL_ACCENT_BORDER = "rgb(194, 218, 231)";

function getTheme2CardSurfaceStyle(isLightTheme, accentBorderColor, isCustomer = false, isTheme2 = false) {
  const darkSurface = isCustomer ? SURFACE_DARK_CUSTOMER : SURFACE_DARK;
  const sizeStyle =
    isCustomer && isTheme2
      ? { minHeight: 150 }
      : isCustomer && !isTheme2
        ? {}
        : { height: "100%", minHeight: 150 };

  return {
    width: "100%",
    ...sizeStyle,
    background: isLightTheme ? SURFACE_LIGHT : darkSurface,
    "--uic2-accent-border": accentBorderColor,
    boxShadow: "none",
  };
}

function getLegacyCardStyle(accentBorderColor, isCustomer = false) {
  return {
    width: "100%",
    borderLeft: `8px solid ${accentBorderColor}`,
    ...(isCustomer ? {} : { height: "100%", minHeight: 150 }),
  };
}

const UserInfoCards = memo(({
  data,
  getActiveColorStyle,
  isCustomer = false,
  isTheme2 = false,
  isLightTheme = false,
  alwaysExpanded = false,
}) => {
  const { t } = useTranslation();
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [legacyCardExpanded, setLegacyCardExpanded] = useState({});
  const [currentTheme, setCurrentTheme] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    return darkMode ? JSON.parse(darkMode) : true;
  });

  const capitalizeWords = (str) => {
    if (!str) return "-";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getTextColor = () => {
    if (isTheme2) {
      return isLightTheme ? "#3c4858" : "#ffffff";
    }
    return currentTheme ? "white" : "black";
  };

  useEffect(() => {
    if (isTheme2) return undefined;

    const handleStorageChange = () => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      setCurrentTheme(newTheme);
    };

    window.addEventListener("storage", handleStorageChange);

    const interval = setInterval(() => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
      }
    }, 100);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [currentTheme, isTheme2]);

  const activeStyle = getActiveColorStyle();

  const getCardStyle = (accentBorderColor) =>
    isTheme2
      ? getTheme2CardSurfaceStyle(isLightTheme, accentBorderColor, isCustomer, isTheme2)
      : getLegacyCardStyle(accentBorderColor, isCustomer);

  const useLegacyMoreDetailsToggle = !isTheme2 && isCustomer;

  const toggleLegacyCard = (cardKey) => {
    setLegacyCardExpanded((prev) => ({
      ...prev,
      [cardKey]: !prev[cardKey],
    }));
  };

  const renderInfoCard = (cardKey, accentColor, title, primaryRows, extraRows = null) => {
    const legacyExpanded = !!legacyCardExpanded[cardKey];
    const showLegacyToggle = useLegacyMoreDetailsToggle && extraRows;

    return (
      <Card
        className={classNames("uic-card", {
          "uic2-card": isTheme2,
          "uic-card--always-expanded": alwaysExpanded && !useLegacyMoreDetailsToggle,
        })}
        style={getCardStyle(accentColor)}
      >
        <CardHeader className="pt-1">
          <h4 className="mb-0">{title}</h4>
        </CardHeader>
        <CardBody
          className={classNames({ "uic2-card-body": isTheme2 })}
          style={
            showLegacyToggle
              ? { paddingTop: 5, display: "flex", flexDirection: "column" }
              : { paddingTop: 5 }
          }
        >
          {isTheme2 ? (
            <div className="uic2-card-body-main">
              {primaryRows}
              {(showAllDetails || alwaysExpanded) && extraRows ? extraRows : null}
            </div>
          ) : showLegacyToggle ? (
            <>
              <div style={{ flexGrow: 1 }}>
                {primaryRows}
                {legacyExpanded ? extraRows : null}
              </div>
              <div style={{ paddingBottom: 10 }}>
                <a
                  href={`#${cardKey}-details`}
                  className="uic-link"
                  style={{ color: activeStyle.borderColor }}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleLegacyCard(cardKey);
                  }}
                >
                  {legacyExpanded
                    ? t("userInfoCards.lessDetails")
                    : t("userInfoCards.moreDetails")}
                </a>
              </div>
            </>
          ) : (
            <>
              <div className="uic-primary-rows">{primaryRows}</div>
              {extraRows ? <div className="uic-extra-rows">{extraRows}</div> : null}
            </>
          )}
        </CardBody>
      </Card>
    );
  };

  const valueStyle = { color: getTextColor() };

  const userExtraRows = (
    <>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.businessPhone")}:</span>
        <span className="aic-value" style={valueStyle}>
          {data?.work_phone || "-"}
        </span>
      </div>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.cellPhone")}:</span>
        <span className="aic-value" style={valueStyle}>
          {data?.cell_phone || "-"}
        </span>
      </div>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.gender")}:</span>
        <span className="aic-value" style={valueStyle}>
          {data?.gender || "-"}
        </span>
      </div>
    </>
  );

  const contactExtraRows = (
    <>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.currentState")}:</span>
        <span className="aic-value" style={valueStyle}>
          {data?.current_address?.state_name
            ? capitalizeWords(data?.current_address?.state_name)
            : "-"}
        </span>
      </div>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.currentZip")}:</span>
        <span className="aic-value" style={valueStyle}>
          {data?.current_address?.zip || "-"}
        </span>
      </div>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.currentCountry")}:</span>
        <span className="aic-value" style={valueStyle}>
          {data?.current_address?.country_name
            ? capitalizeWords(data?.current_address?.country_name)
            : "-"}
        </span>
      </div>
    </>
  );

  const employmentExtraRows = (
    <>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.employerCity")}:</span>
        <span className="aic-value" style={valueStyle}>
          {readEmploymentField(data?.current_employment, "employment_city", "city")
            ? capitalizeWords(
                readEmploymentField(data?.current_employment, "employment_city", "city")
              )
            : "-"}
        </span>
      </div>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.employerCountry")}:</span>
        <span className="aic-value" style={valueStyle}>
          {readEmploymentField(data?.current_employment, "employment_country", "country")
            ? capitalizeWords(
                readEmploymentField(
                  data?.current_employment,
                  "employment_country",
                  "country"
                )
              )
            : "-"}
        </span>
      </div>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.currentPosition")}:</span>
        <span className="aic-value" style={valueStyle}>
          {readEmploymentField(data?.current_employment, "employment_position", "position") ||
            "-"}
        </span>
      </div>
      <div className="aic-row">
        <span className="aic-label">{t("userInfoCards.currentType")}:</span>
        <span className="aic-value" style={valueStyle}>
          {readEmploymentField(data?.current_employment, "employment_type", "type") || "-"}
        </span>
      </div>
    </>
  );

  return (
    <div
      className={classNames("d-flex flex-column flex-lg-row uic-info-cards-row", {
        "users-info-cards-theme2": isTheme2,
        "users-info-cards-theme2--light": isTheme2 && isLightTheme,
        "users-info-cards-theme2--dark": isTheme2 && !isLightTheme,
      })}
      style={{ gap: 12, alignItems: isCustomer && !isTheme2 ? "flex-start" : "stretch" }}
      onMouseEnter={isTheme2 && !alwaysExpanded ? () => setShowAllDetails(true) : undefined}
      onMouseLeave={isTheme2 && !alwaysExpanded ? () => setShowAllDetails(false) : undefined}
    >
      {renderInfoCard(
        "user",
        activeStyle.borderColor,
        t("userInfoCards.userDetail"),
        <>
          <div className="aic-row">
            <span className="aic-label">{t("userInfoCards.fullName")}:</span>
            <span className="aic-value" style={valueStyle}>
              {data?.full_name ? capitalizeWords(data?.full_name) : "-"}
            </span>
          </div>
          <div className="aic-row">
            <span className="aic-label">{t("userInfoCards.email")}:</span>
            <span className="aic-value" style={valueStyle}>
              {data?.email || "-"}
            </span>
          </div>
          <div className="aic-row">
            <span className="aic-label">{t("userInfoCards.homePhone")}:</span>
            <span className="aic-value" style={valueStyle}>
              {data?.home_phone || "-"}
            </span>
          </div>
        </>,
        userExtraRows
      )}

      {isCustomer && (
        <>
          {renderInfoCard(
            "address",
            NEUTRAL_ACCENT_BORDER,
            t("userInfoCards.currentAddress"),
            <>
              <div className="aic-row">
                <span className="aic-label">{t("userInfoCards.currentAddress1")}:</span>
                <span className="aic-value" style={valueStyle}>
                  {data?.current_address?.address_1 || "-"}
                </span>
              </div>
              <div className="aic-row">
                <span className="aic-label">{t("userInfoCards.currentAddress2")}:</span>
                <span className="aic-value" style={valueStyle}>
                  {data?.current_address?.address_2 || "-"}
                </span>
              </div>
              <div className="aic-row">
                <span className="aic-label">{t("userInfoCards.currentCity")}:</span>
                <span className="aic-value" style={valueStyle}>
                  {data?.current_address?.city
                    ? capitalizeWords(data?.current_address?.city)
                    : "-"}
                </span>
              </div>
            </>,
            contactExtraRows
          )}

          {renderInfoCard(
            "employment",
            NEUTRAL_ACCENT_BORDER,
            t("userInfoCards.currentEmployment"),
            <>
              <div className="aic-row">
                <span className="aic-label">{t("userInfoCards.currentEmployer")}:</span>
                <span className="aic-value" style={valueStyle}>
                  {readEmploymentField(data?.current_employment, "employment_employer", "employer") ||
                    "-"}
                </span>
              </div>
              <div className="aic-row">
                <span className="aic-label">{t("userInfoCards.employerAddress1")}:</span>
                <span className="aic-value" style={valueStyle}>
                  {readEmploymentField(
                    data?.current_employment,
                    "employment_address_1",
                    "address_1"
                  ) || "-"}
                </span>
              </div>
              <div className="aic-row">
                <span className="aic-label">{t("userInfoCards.employerAddress2")}:</span>
                <span className="aic-value" style={valueStyle}>
                  {readEmploymentField(
                    data?.current_employment,
                    "employment_address_2",
                    "address_2"
                  ) || "-"}
                </span>
              </div>
            </>,
            employmentExtraRows
          )}
        </>
      )}
    </div>
  );
});

UserInfoCards.displayName = "UserInfoCards";

export default UserInfoCards;
